import React, { useState } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

const LanguageSelector = ({ variant = 'default' }) => {
  const { language, setLanguage, languages, getCurrentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const currentLang = getCurrentLanguage();

  const handleSelect = (code) => {
    setLanguage(code);
    setIsOpen(false);
  };

  if (variant === 'minimal') {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-sm bg-zinc-800/50 hover:bg-zinc-800 transition-colors text-sm"
          data-testid="language-selector-minimal"
        >
          <span className="text-base">{currentLang.flag}</span>
          <span className="text-zinc-400 hidden sm:inline">{currentLang.code.toUpperCase()}</span>
          <ChevronDown className={`w-3 h-3 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        <AnimatePresence>
          {isOpen && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setIsOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 top-full mt-1 w-48 bg-zinc-900 border border-zinc-800 rounded-sm shadow-xl z-50 overflow-hidden"
              >
                <div className="max-h-64 overflow-y-auto">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleSelect(lang.code)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                        language === lang.code 
                          ? 'bg-orange-600/20 text-orange-500' 
                          : 'text-zinc-300 hover:bg-zinc-800'
                      }`}
                      data-testid={`lang-option-${lang.code}`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{lang.native}</p>
                        <p className="text-xs text-zinc-500">{lang.name}</p>
                      </div>
                      {language === lang.code && (
                        <Check className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Default full variant
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-sm bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 transition-colors"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4 text-zinc-400" />
        <span className="text-lg">{currentLang.flag}</span>
        <span className="text-sm text-zinc-300">{currentLang.native}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute left-0 top-full mt-2 w-56 bg-zinc-900 border border-zinc-800 rounded-sm shadow-2xl z-50 overflow-hidden"
            >
              <div className="p-2 border-b border-zinc-800">
                <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider px-2">
                  Select Language
                </p>
              </div>
              <div className="max-h-72 overflow-y-auto p-1">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleSelect(lang.code)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-left transition-all ${
                      language === lang.code 
                        ? 'bg-orange-600 text-white' 
                        : 'text-zinc-300 hover:bg-zinc-800'
                    }`}
                    data-testid={`lang-option-${lang.code}`}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{lang.native}</p>
                      <p className={`text-xs ${language === lang.code ? 'text-orange-200' : 'text-zinc-500'}`}>
                        {lang.name}
                      </p>
                    </div>
                    {language === lang.code && (
                      <Check className="w-4 h-4 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;
